import React, { useState, useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import Header from './components/Header';
import Footer from './components/Footer';
import ScrollToTop from './components/ScrollToTop';
import Home from './pages/Home';
import { AuthProvider } from './context/AuthContext';
import './App.css';

// Lazy load routes to reduce main bundle size
const RentCatalog = lazy(() => import('./pages/RentCatalog'));
const SaleCatalog = lazy(() => import('./pages/SaleCatalog'));
const PropertyDetails = lazy(() => import('./pages/PropertyDetails'));
const WealthPropertyDetails = lazy(() => import('./pages/WealthPropertyDetails'));
const FeaturedPropertyCatalog = lazy(() => import('./pages/FeaturedPropertyCatalog'));
const ProjectCatalog = lazy(() => import('./pages/ProjectCatalog'));
const ProjectDetails = lazy(() => import('./pages/ProjectDetails'));
const WealthProjectDetails = lazy(() => import('./pages/WealthProjectDetails'));
const PublicProfile = lazy(() => import('./pages/PublicProfile'));
const MapView = lazy(() => import('./pages/MapView'));
const Login = lazy(() => import('./pages/auth/Login'));
const Register = lazy(() => import('./pages/auth/Register'));
const ForgotPassword = lazy(() => import('./pages/auth/ForgotPassword'));
const AdminLogin = lazy(() => import('./pages/auth/AdminLogin'));
const DynamicPage = lazy(() => import('./pages/DynamicPage'));
const AboutUs = lazy(() => import('./pages/AboutUs'));
const ContactUs = lazy(() => import('./pages/ContactUs'));
const TermsAndConditions = lazy(() => import('./pages/TermsAndConditions'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const DashboardLayout = lazy(() => import('./pages/dashboard/DashboardLayout'));
const DashboardProfile = lazy(() => import('./pages/dashboard/DashboardProfile'));
const PostProperty = lazy(() => import('./pages/dashboard/PostProperty'));
const PostProject = lazy(() => import('./pages/dashboard/PostProject'));
const MyProperties = lazy(() => import('./pages/dashboard/MyProperties'));
const MyProjects = lazy(() => import('./pages/dashboard/MyProjects'));
const MyFavorites = lazy(() => import('./pages/dashboard/MyFavorites'));
const MyEnquiries = lazy(() => import('./pages/dashboard/MyEnquiries'));
const ManageEnquiries = lazy(() => import('./pages/dashboard/ManageEnquiries'));
const Settings = lazy(() => import('./pages/dashboard/Settings'));
const MyBids = lazy(() => import('./pages/dashboard/MyBids'));
const MyPackages = lazy(() => import('./pages/dashboard/MyPackages'));
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const BuilderCatalog = lazy(() => import('./pages/BuilderCatalog'));
const SlideshowPage = lazy(() => import('./pages/SlideshowPage'));
const WealthLogoPage = lazy(() => import('./pages/WealthLogoPage'));
const DownloadApp = lazy(() => import('./pages/DownloadApp'));

const AppContent = ({ settings }) => {
  const location = useLocation();
  const hideHeaderFooter = location.pathname === '/wa' || location.pathname === '/download-app';

  return (
    <>
      <ScrollToTop />
      {settings && (
        <Helmet>
          <title>{settings.seoTitle}</title>
          <meta name="description" content={settings.seoDescription} />
          <meta name="keywords" content={settings.seoKeywords} />
          {settings.googleAnalyticsCode && (
            <script async src={`https://www.googletagmanager.com/gtag/js?id=${settings.googleAnalyticsCode}`}></script>
          )}
          {settings.googleAnalyticsCode && (
            <script>
              {`
                window.dataLayer = window.dataLayer || [];
                function gtag(){dataLayer.push(arguments);}
                gtag('js', new Date());
                gtag('config', '${settings.googleAnalyticsCode}');
              `}
            </script>
          )}
          {settings.googleWebmasterCode && (
            <meta name="google-site-verification" content={settings.googleWebmasterCode} />
          )}
        </Helmet>
      )}
      <div className="App flex flex-col" style={{ minHeight: '100vh' }}>
        {!hideHeaderFooter && <Header />}
        {!hideHeaderFooter && settings && (
          <div className="visits-marquee-container">
            <div className="visits-marquee">
              <span className="visits-item">Daily Visits: <span>{settings.dailyVisitors || 0}</span></span>
              <span className="visits-separator">•</span>
              <span className="visits-item">Monthly Visits: <span>{settings.monthlyVisitors || 0}</span></span>
              <span className="visits-separator">•</span>
              <span className="visits-item">Yearly Visits: <span>{settings.yearlyVisitors || 0}</span></span>
              <span className="visits-separator">•</span>
              <span className="visits-item">Total Visits: <span>{settings.totalVisitors || 0}</span></span>
              <span className="visits-separator">&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</span>
              <span className="visits-item">Daily Visits: <span>{settings.dailyVisitors || 0}</span></span>
              <span className="visits-separator">•</span>
              <span className="visits-item">Monthly Visits: <span>{settings.monthlyVisitors || 0}</span></span>
              <span className="visits-separator">•</span>
              <span className="visits-item">Yearly Visits: <span>{settings.yearlyVisitors || 0}</span></span>
              <span className="visits-separator">•</span>
              <span className="visits-item">Total Visits: <span>{settings.totalVisitors || 0}</span></span>
            </div>
          </div>
        )}
        <main style={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
          <Suspense fallback={
            <div className="flex justify-center items-center h-64 w-full">
              <svg className="animate-spin h-10 w-10 text-primary" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            </div>
          }>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/rent" element={<RentCatalog />} />
              <Route path="/properties" element={<SaleCatalog />} />
              <Route path="/properties/:seoSlug" element={<SaleCatalog />} />
              <Route path="/properties/featured" element={<FeaturedPropertyCatalog />} />
              <Route path="/map" element={<MapView />} />
              <Route path="/projects" element={<ProjectCatalog />} />
              <Route path="/projects/:seoSlug" element={<ProjectCatalog />} />
              <Route path="/builders" element={<BuilderCatalog />} />
              <Route path="/projects/:id" element={<ProjectDetails />} />
              <Route path="/project/:slug/:id" element={<ProjectDetails />} />
              <Route path="/wealth-project/:id" element={<WealthProjectDetails />} />
              <Route path="/wealth-project/:slug/:id" element={<WealthProjectDetails />} />
              <Route path="/profile/builder/:id/:slug" element={<PublicProfile />} />
              <Route path="/contact-us" element={<ContactUs />} />
              <Route path="/page/about-us" element={<AboutUs />} />
              <Route path="/terms" element={<TermsAndConditions />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/page/:slug" element={<DynamicPage />} />
              <Route path="/property/:id" element={<PropertyDetails />} />
              <Route path="/property/:slug/:id" element={<PropertyDetails />} />
              <Route path="/wealth-property/:id" element={<WealthPropertyDetails />} />
              <Route path="/wealth-property/:slug/:id" element={<WealthPropertyDetails />} />
              <Route path="/profile/:role/:id/:name" element={<PublicProfile />} />
              <Route path="/slideshow" element={<SlideshowPage />} />
              <Route path="/wa" element={<WealthLogoPage />} />
              <Route path="/download-app" element={<DownloadApp />} />
              
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/system-portal" element={<AdminLogin />} />
              
              {/* Protected Dashboard Routes */}
              <Route path="/dashboard" element={<DashboardLayout />}>
                <Route index element={<DashboardProfile />} />
                <Route path="favorites" element={<MyFavorites />} />
                <Route path="enquiries" element={<MyEnquiries />} />
                <Route path="my-properties" element={<MyProperties />} />
                <Route path="my-projects" element={<MyProjects />} />
                <Route path="add-property" element={<PostProperty />} />
                <Route path="edit-property/:id" element={<PostProperty />} />
                <Route path="add-project" element={<PostProject />} />
                <Route path="edit-project/:id" element={<PostProject />} />
                <Route path="manage-enquiries" element={<ManageEnquiries />} />
                <Route path="my-bids" element={<MyBids />} />
                <Route path="my-packages" element={<MyPackages />} />
                <Route path="settings" element={<Settings />} />
              </Route>
              
              {/* Admin Routes */}
              <Route path="/admin" element={<AdminDashboard />} />
              
              <Route path="*" element={<Navigate to="/" />} />
            </Routes>
          </Suspense>
        </main>
        {!hideHeaderFooter && <Footer />}
      </div>
    </>
  );
};

function App() {
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    const initApp = async () => {
      try {
        let currentSettings = null;
        const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/settings`);
        currentSettings = res.data;

        if (!sessionStorage.getItem('visitTracked')) {
          const trackRes = await axios.post(`${process.env.REACT_APP_API_URL}/api/settings/track-visit`);
          sessionStorage.setItem('visitTracked', 'true');
          
          if (currentSettings && trackRes.data.success) {
            currentSettings = {
              ...currentSettings,
              totalVisitors: trackRes.data.totalVisitors,
              dailyVisitors: trackRes.data.dailyVisitors,
              monthlyVisitors: trackRes.data.monthlyVisitors,
              yearlyVisitors: trackRes.data.yearlyVisitors
            };
          }
        }
        setSettings(currentSettings);
      } catch (err) {
        console.error('Failed to initialize app settings', err);
      }
    };

    initApp();
  }, []);

  return (
    <AuthProvider>
      <Router>
        <AppContent settings={settings} />
      </Router>
    </AuthProvider>
  );
}

export default App;
